import {
  ICategories,
  IRawInquiriesResData,
  IRawInquiryItem,
  IService,
  IServiceCard,
  TInquiriesKey,
} from "@/interfaces";
import { cn, nanoid } from "@/lib";

import { JSX } from "react";
import {
  BdBanknote2,
  BdBillList,
  BdClipboardText,
  BdDocument,
  BdMailbox,
  BdPlate,
  BdCalculator,
  BdWadOfMoney,
} from "solar-icon-react/bd";
import { getClientServiceKeyByAPIFlag } from "./serviceFlagAdapter";
import { globalRawServicesData } from "@/constants/globalRawServicesData/globalRawServicesData";

const CATEGORY_MAP: Record<TInquiriesKey, ICategories> = {
  "1": {
    id: nanoid(),
    category: "bankingServices",
    variant: "emerald-mint",
    key: "banking",
    href: "#",
    icon: <BdBanknote2 size={30} className={cn("text-primary-main!")} />,
  },
  "2": {
    id: nanoid(),
    category: "vehicleServices",
    variant: "sky-bliss",
    key: "vehicle",
    href: "#",
    icon: <BdPlate size={30} className={cn("text-primary-main!")} />,
  },
  "3": {
    id: nanoid(),
    category: "governmentServices",
    variant: "teal-ocean",
    key: "government",
    href: "#",
    icon: <BdClipboardText size={30} className={cn("text-primary-main!")} />,
  },
  "4": {
    id: nanoid(),
    category: "legalServices",
    variant: "sunset-gold",
    key: "legal",
    href: "#",
    icon: <BdMailbox size={30} className={cn("text-primary-main!")} />,
  },
  "6": {
    id: nanoid(),
    category: "billsServices",
    variant: "rose-500",
    key: "bills",
    href: "#",
    icon: <BdBillList size={30} className={cn("text-primary-main!")} />,
  },
  "7": {
    id: nanoid(),
    category: "freeServices",
    variant: "yellow-500",
    key: "free",
    href: "#",
    icon: <BdWadOfMoney size={30} className={cn("text-primary-main!")} />,
  },
};

const STATIC_FREE_SERVICES: IService[] = [
  {
    id: "static-convert-money",
    label: globalRawServicesData.Convert_Money_Inquiry.label.adapted,
    icon: globalRawServicesData.Convert_Money_Inquiry.icon as JSX.Element,
    disabled: "",
    href: globalRawServicesData.Convert_Money_Inquiry.href.rewrite,
    flag: "convert_money",
  },
  {
    id: "static-pelak-yab",
    label: globalRawServicesData.Pelak_Yab_Inquiry.label.adapted,
    icon: globalRawServicesData.Pelak_Yab_Inquiry.icon as JSX.Element,
    disabled: "",
    href: globalRawServicesData.Pelak_Yab_Inquiry.href.rewrite,
    flag: "pelak_yab",
  },
  {
    id: "static-deposit-interest-calculator",
    label: globalRawServicesData.Deposit_Interest_Calculator.label.adapted,
    icon: globalRawServicesData.Deposit_Interest_Calculator.icon as JSX.Element,
    disabled: "",
    href: globalRawServicesData.Deposit_Interest_Calculator.href.rewrite,
    flag: "deposit_interest_calculator",
  },
  {
    id: "static-age-calculator",
    label: globalRawServicesData.Age_Calculator.label.adapted,
    icon: globalRawServicesData.Age_Calculator.icon as JSX.Element,
    disabled: "",
    href: globalRawServicesData.Age_Calculator.href.rewrite,
    flag: "age_calculator",
  },
  {
    id: "static-payroll-calculator-1405",
    label: globalRawServicesData.Payroll_Calculator_1405.label.adapted,
    icon: globalRawServicesData.Payroll_Calculator_1405.icon as JSX.Element,
    disabled: "",
    href: globalRawServicesData.Payroll_Calculator_1405.href.rewrite,
    flag: "payroll_calculator_1405",
  },
];

const SERVICE_LABEL_ALIAS_MAP: Partial<Record<string, string>> = {
  returned_check: "استعلام چک برگشتی",
  returned_check_details: "استعلام چک برگشتی با جزئیات",
  back_cheques: "استعلام چک برگشتی با جزئیات کامل",
  loan_installment_calculator: "محاسبه اقساط وام",
  inflation_calculator: "ماشین حساب تورم",
  deposit_interest_calculator: "محاسبه سود سپرده بانکی",
  age_calculator: "محاسبه سن آنلاین",
  payroll_calculator_1405: "ماشین حساب حقوق و دستمزد ۱۴۰۵",
  toll: "استعلام عوارض آزادراهی",
  freeway_toll: "استعلام عوارض آزادراهی",
  vehicle_info: "استعلام مشخصات و مدل خودرو",
  vehicle_comprehensive_report: "گزارش جامع وضعیت خودرو",
  tpi: "استعلام سوابق بیمه‌نامه شخص ثالث",
};

const SERVICE_HREF_ALIAS_MAP: Partial<Record<string, string>> = {
  internal_credit_score:
    "/services/" +
    encodeURIComponent(
      "\u0627\u0639\u062a\u0628\u0627\u0631\u0633\u0646\u062c\u06cc",
    ),
  back_cheques: "/services/استعلام-چک-برگشتی-با-جزئیات-کامل",
  loan_installment_calculator: "/services/محاسبه-اقساط-وام",
  inflation_calculator: "/services/ماشین-حساب-تورم",
  deposit_interest_calculator: "/services/محاسبه-سود-سپرده-بانکی",
  age_calculator: "/services/محاسبه-سن-آنلاین",
  payroll_calculator_1405: "/services/ماشین-حساب-حقوق-و-دستمزد-۱۴۰۵",
  toll: "/services/استعلام-عوارض-آزادراهی",
  freeway_toll: "/services/استعلام-عوارض-آزادراهی",
  vehicle_info: "/services/استعلام-مشخصات-و-مدل-خودرو",
  vehicle_comprehensive_report: "/services/استعلام-جامع-خودرو",
  tpi: globalRawServicesData.Third_Party_Insurance.href.rewrite,
};

const SERVICE_ICON_ALIAS_MAP: Partial<Record<string, JSX.Element>> = {
  internal_credit_score: globalRawServicesData.Bank_Validation.icon ?? (
    <BdDocument />
  ),
  back_cheques: globalRawServicesData.Bounced_Check.icon ?? <BdDocument />,
  loan_installment_calculator: <BdCalculator className="h-full! w-full!" />,
  inflation_calculator: <BdCalculator className="h-full! w-full!" />,
  deposit_interest_calculator: <BdCalculator className="h-full! w-full!" />,
  age_calculator: globalRawServicesData.Age_Calculator.icon ?? (
    <BdCalculator className="h-full! w-full!" />
  ),
  payroll_calculator_1405: <BdCalculator className="h-full! w-full!" />,
  vehicle_info: globalRawServicesData.Vehicle_Info.icon ?? (
    <BdPlate className="h-full! w-full!" />
  ),
  vehicle_comprehensive_report: <BdPlate className="h-full! w-full!" />,
  tpi: globalRawServicesData.Third_Party_Insurance.icon ?? (
    <BdPlate className="h-full! w-full!" />
  ),
};

const HIDDEN_SERVICE_FLAGS = new Set([
  "back_cheques_authorize",
  "back_cheques_verify",
  "toll",
]);

const getServiceHref = (
  item: IRawInquiryItem,
  fallbackHref: string,
): string => {
  const seoUrl = item.seo_url?.trim();
  if (!seoUrl) return fallbackHref;
  if (seoUrl.startsWith("/")) return seoUrl;
  if (/^https?:\/\//i.test(seoUrl)) return seoUrl;
  return `/services/${seoUrl}`;
};

export const getInquiriesAdapter = (
  data: IRawInquiriesResData | undefined | null,
): IServiceCard[] => {
  if (!data || typeof data !== "object") return [];

  const cards = Object.entries<IRawInquiryItem[]>(data).flatMap(
    ([categoryId, items]) => {
      const categoryData = CATEGORY_MAP[categoryId as TInquiriesKey];

      if (!categoryData) {
        console.warn("دسته‌بندی ناشناخته از API دریافت شد:", categoryId);
        return [];
      }

      const { category, variant, icon, id } = categoryData;

      const services: IService[] = items
        .filter(
          ({ flag, status }) =>
            status !== "unavailability" && !HIDDEN_SERVICE_FLAGS.has(flag),
        )
        .map((item): IService => {
          const serviceKey = getClientServiceKeyByAPIFlag(item.flag);
          const serviceHrefAlias = SERVICE_HREF_ALIAS_MAP[item.flag];

          if (serviceHrefAlias) {
            return {
              id: item.id,
              label: SERVICE_LABEL_ALIAS_MAP[item.flag] ?? item.name,
              icon: SERVICE_ICON_ALIAS_MAP[item.flag] ?? <BdDocument />,
              disabled: item.status,
              href: getServiceHref(item, serviceHrefAlias),
              tag: item.tag,
              flag: item.flag,
            };
          }

          if (!serviceKey) {
            console.warn("عدم مطابقت بین فلگ و سرویس:", item.flag);
            return {
              id: item.id,
              label: item.name,
              icon: <BdDocument className="h-full! w-full!" />,
              disabled: "inactive",
              href: "#",
              tag: item.tag,
              flag: item.flag,
            };
          }

          const service = globalRawServicesData[serviceKey];
          return {
            id: item.id,
            label: SERVICE_LABEL_ALIAS_MAP[item.flag] ?? item.name,
            icon: service.icon ?? <BdDocument />,
            disabled: item.status,
            href: getServiceHref(item, service.href.rewrite),
            tag: item.tag,
            flag: item.flag,
          };
        })
        .sort((first, second) => {
          if (categoryData.key !== "vehicle") return 0;
          if (first.flag === "freeway_toll") return 1;
          if (second.flag === "freeway_toll") return -1;
          return 0;
        });

      return [
        {
          id,
          category,
          services,
          variant,
          categoryIcon: icon,
        },
      ];
    },
  );

  // const freeCardIndex = cards.findIndex((c) => c.category === "freeServices");
  // if (freeCardIndex !== -1) {
  //   cards[freeCardIndex] = {
  //     ...cards[freeCardIndex],
  //     services: [...STATIC_FREE_SERVICES, ...cards[freeCardIndex].services],
  //   };
  // } else {
  //   const { category, variant, icon, id } = CATEGORY_MAP["5"];
  //   cards.push({
  //     id,
  //     category,
  //     services: STATIC_FREE_SERVICES,
  //     variant,
  //     categoryIcon: icon,
  //   });
  // }

  return cards;
};
